import { RegisterAgencyPayload, GenerateOtpPayload, CachedAgencyFormData } from '../../types';
import { transformFile } from 'utils/file';
import { CountryCode } from 'libphonenumber-js';
import { getFromLocal, getFromSession, removeFromSession, saveToLocal, saveToSession } from 'utils/cache';
import { UploadFile } from 'antd/es/upload/interface';

type SelectedCountry = {
  code: CountryCode;
  iso: string | undefined;
};

export const TEMP_CREDENTIALS_KEY = 'temp_user';

export interface TempCredentials {
  email: string;
  password: string;
}

export const createAgencyFormDataWithOtp = (
  values: RegisterAgencyPayload,
  selectedCountry: SelectedCountry,
  otp: string,
  logoFileList: UploadFile[],
): FormData => {
  const agencyDTO = {
    agencyName: values.companyName,
    countryId: values.countryId,
    currencyCode: values.currencyCode.split(' ')[0],
    currencyCodeIso: values.currencyCode.split(' ')[1],
    registrationNumber: values.registrationNumber,
    address: values.address,
    email: values.email,
    fullname: values.name,
    contactNumber: values.contactNumber,
    phoneCountryCode: selectedCountry.code,
    phoneCountryIso: selectedCountry.iso,
    password: values.password,
    preferredLanguage: values.preferredLanguage,
    otp: otp,
  };

  const formData = new FormData();
  formData.append('agencyDTO', JSON.stringify(agencyDTO));

  // Get the logo file from the context fileList
  const logoFile = logoFileList.length > 0 ? logoFileList[0].originFileObj : undefined;
  if (logoFile) {
    const transformedFile = transformFile(logoFile);
    formData.append('logo', transformedFile);
  }

  return formData;
};

export const CACHED_AGENCY_FORM_DATA_KEY = 'signup_basic_info_form_data';
export const CONTRACT_EMAIL_KEY = 'contract_email';

/**
 * Get a specific field from cached agency form data
 * @param fieldName - The name of the field to extract from formValues
 * @returns The field value as string or empty string if not found
 */
export const getFieldFromCachedFormData = (fieldName: string): string => {
  try {
    const cachedData = getFromLocal(CACHED_AGENCY_FORM_DATA_KEY) as {
      formValues: Record<string, string | number | boolean>;
    } | null;

    return String(cachedData?.formValues?.[fieldName] || '');
  } catch (error) {
    return '';
  }
};

/**
 * Get email from cached agency form data
 * @returns email string or empty string if not found
 */
export const getEmailFromCachedFormData = (): string => {
  return getFieldFromCachedFormData('email');
};

/**
 * Get fullName from cached agency form data
 * @returns fullName string or empty string if not found
 */
export const getFullNameFromCachedFormData = (): string => {
  return getFieldFromCachedFormData('name');
};

/**
 * Get cached form data and selected country
 * @returns object with formValues and selectedCountry or null if not found
 */
export const getCachedFormData = () => {
  try {
    const cachedData = getFromLocal(CACHED_AGENCY_FORM_DATA_KEY) as {
      formValues: RegisterAgencyPayload;
      selectedCountry: SelectedCountry;
    } | null;

    return cachedData;
  } catch (error) {
    return null;
  }
};

/**
 * Get email from contract email cache
 * @returns email string or empty string if not found
 */
export const getContractEmail = (): string => {
  try {
    return String(getFromLocal(CONTRACT_EMAIL_KEY) || '');
  } catch (error) {
    return '';
  }
};

/**
 * Prepare OTP payload and cache form data for agency registration
 * @param values - Form values from RegisterAgencyPayload
 * @param selectedCountry - Selected country with code and iso
 * @param logoFileList - Array of uploaded logo files
 * @returns Object containing the OTP payload and cached data
 */
export const prepareOtpPayloadAndCacheData = (
  values: RegisterAgencyPayload,
  selectedCountry: { code: CountryCode; iso: string | undefined },
  _logoFileList: UploadFile[],
): { otpPayload: GenerateOtpPayload; cachedData: CachedAgencyFormData } => {
  // Prepare data for the generateOtp API
  const otpPayload: GenerateOtpPayload = {
    agencyName: values.companyName,
    countryId: values.countryId,
    currencyCode: values.currencyCode.split(' ')[0],
    currencyCodeIso: values.currencyCode.split(' ')[1],
    registrationNumber: values.registrationNumber,
    address: values.address,
    email: values.email,
    fullname: values.name,
    contactNumber: values.contactNumber,
    phoneCountryCode: selectedCountry.code,
    phoneCountryIso: selectedCountry.iso || '',
    password: values.password,
    preferredLanguage: values.preferredLanguage,
  };

  // Cache the raw form values and selected country for restoration
  // Note: We exclude logo from caching since we're now using context
  const valuesWithoutLogo = { ...values };
  delete valuesWithoutLogo.logo;

  const cachedData: CachedAgencyFormData = {
    formValues: valuesWithoutLogo,
    selectedCountry: selectedCountry,
    logoInfo: undefined, // No longer caching logo info in localStorage
  };

  // Save to local storage
  saveToLocal(CACHED_AGENCY_FORM_DATA_KEY, cachedData);

  saveTempCredentials(values.email, values.password);

  return { otpPayload, cachedData };
};

export const saveTempCredentials = (email: string, password: string): void => {
  const credentials: TempCredentials = {
    email,
    password,
  };

  // Use existing encrypted storage
  saveToSession(TEMP_CREDENTIALS_KEY, credentials, true, true);
};

export const getTempCredentials = (): TempCredentials | null => {
  const credentials = getFromSession(TEMP_CREDENTIALS_KEY, true, true) as TempCredentials | null;

  if (!credentials) return null;

  return credentials;
};

export const clearTempCredentials = (): void => {
  removeFromSession(TEMP_CREDENTIALS_KEY);
};
